// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_CONFIG } from '../config/api';
import { safeDecodeJwtPayload, clearAuthSessionStorage, notifyAuthSessionChanged } from '../utils/authUtils';
import { subscribePushNotifications } from '../services/pushSubscription';

// Create Auth Context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API Base URL - Use centralized API configuration
  const API_BASE_URL = API_CONFIG.BASE_URL;

  // Create a ref to store the logout function
  const logoutRef = useRef();

  // ------------------------------------------------------------
  // Hard logout (no API calls) + redirect storm prevention
  // ------------------------------------------------------------
  const redirectingRef = useRef(false);

  const getAppBasename = useCallback(() => {
    const path = window.location?.pathname || '';
    if (path.startsWith('/ptthaiplus_app')) return '/ptthaiplus_app';
    return '';
  }, []);

  const forceLogout = useCallback((reason = 'unauthorized') => {
    try {
      clearAuthSessionStorage();
    } catch (_) {
      // ignore storage errors; redirect still needed
    }

    setUser(null);
    setError(null);
    notifyAuthSessionChanged(reason);

    // Redirect (once) to sign-in route within basename.
    if (redirectingRef.current) return;
    redirectingRef.current = true;

    const basename = getAppBasename();
    const target = `${basename}/signin`;
    const currentPath = window.location?.pathname || '';
    if (!currentPath.endsWith('/signin')) {
      window.location.assign(target);
    }
  }, [getAppBasename]);

  // ------------------------------------------------------------
  // Refresh token single-flight (prevents parallel refresh storms)
  // ------------------------------------------------------------
  const refreshInFlightRef = useRef(null);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const run = (async () => {
      try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          // Refresh token itself is invalid/expired -> stop immediately.
          forceLogout('refresh_401');
          throw new Error(errorData.message || 'REFRESH_TOKEN_EXPIRED');
        }
        throw new Error(errorData.message || 'Token refresh failed');
      }

      const data = await response.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        return data.accessToken;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Never call logout() here (it can trigger apiCall -> refreshToken loop).
      // For refresh failures, clear local state and redirect once.
      forceLogout('refresh_failed');
      throw error;
    } finally {
      refreshInFlightRef.current = null;
    }
    })();

    refreshInFlightRef.current = run;
    return run;
  }, [API_BASE_URL, forceLogout]);

  // Check if token is expired or about to expire (within 5 minutes)
  // Uses safe decode to avoid InvalidCharacterError when token is malformed
  const isTokenExpiringSoon = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return true;

    const payload = safeDecodeJwtPayload(token);
    if (!payload || !payload.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = payload.exp - currentTime;
    return timeUntilExpiry < 300;
  }, []);

  // API call helper with error handling and automatic token refresh
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      // Check if token is expiring soon and refresh proactively
      if (!options.skipAuth && isTokenExpiringSoon()) {
        try {
          await refreshToken();
        } catch (refreshError) {
          console.warn('Proactive token refresh failed, continuing with current token:', refreshError);
        }
      }

      const token = localStorage.getItem('authToken');
      const defaultHeaders = {
        'Content-Type': 'application/json',
      };

      if (token && !options.skipAuth) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          ...defaultHeaders,
          ...options.headers
        },
        signal: controller.signal,
        ...options
      });

      clearTimeout(timeoutId);

      if (response.status === 401) {
        // Token expired, try to refresh
        if (!options.skipAuth) {
          try {
            const newToken = await refreshToken();
            // Retry the request with new token
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: options.method || 'GET',
              headers: {
                ...defaultHeaders,
                'Authorization': `Bearer ${newToken}`,
                ...options.headers
              },
              ...options
            });

            if (!retryResponse.ok) {
              const errorData = await retryResponse.json().catch(() => ({}));
              throw new Error(errorData.message || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
            }

            return await retryResponse.json();
          } catch (refreshError) {
            // Refresh failed -> force logout already handled inside refreshToken()
            throw new Error('Session expired - please login again');
          }
        } else {
          // Can't refresh if skipAuth is true
          throw new Error('Unauthorized');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }, [API_BASE_URL, refreshToken, isTokenExpiringSoon]);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const savedUser = localStorage.getItem('authUser');
        const token = localStorage.getItem('authToken');

        if (savedUser && token) {
          const parsed = JSON.parse(savedUser);
          setUser(parsed);
          
          // Verify token with server
          try {
            await apiCall('/auth/verify');

            // Re-subscribe push notifications on app reopen (non-blocking)
            if (parsed.id && 'serviceWorker' in navigator && 'Notification' in window && Notification.permission === 'granted') {
              subscribePushNotifications(parsed.id).catch(() => {});
            }
          } catch (error) {
            console.warn('Token verification failed:', error.message);
            clearAuthSessionStorage();
            setUser(null);
            notifyAuthSessionChanged('verify_failed');
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setError('Authentication check failed');
        clearAuthSessionStorage();
        setUser(null);
        notifyAuthSessionChanged('auth_check_failed');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [apiCall]);

  // Login function
  const login = useCallback(async (email, password, rememberMe = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall('/auth/login', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ email, password, rememberMe: Boolean(rememberMe) })
      });

      if (response.success && response.user && (response.token || response.accessToken)) {
        const userData = {
          id: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName || response.user.first_name,
          lastName: response.user.lastName || response.user.last_name,
          role: response.user.role,
          avatar: response.user.avatar,
          isVerified: response.user.isVerified || response.user.is_verified,
          createdAt: response.user.createdAt || response.user.created_at
        };

        setUser(userData);
        localStorage.setItem('authUser', JSON.stringify(userData));
        localStorage.setItem('authToken', response.accessToken || response.token);
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }

        // Re-subscribe push (only if permission already granted — no prompt)
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          subscribePushNotifications(userData.id).catch(() => {});
        }

        return { success: true, user: userData };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = error.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Register function
  const register = useCallback(async (userData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall('/auth/register', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(userData)
      });

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = error.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    
    try {
      // Call logout endpoint if token exists
      const token = localStorage.getItem('authToken');
      if (token) {
        await apiCall('/auth/logout', {
          method: 'POST'
        }).catch(err => {
          console.warn('Logout API call failed:', err.message);
          // Continue with local logout even if API fails
        });
      }
    } catch (error) {
      console.warn('Logout error:', error.message);
    } finally {
      setUser(null);
      setError(null);
      clearAuthSessionStorage();
      notifyAuthSessionChanged('logout');
      setLoading(false);
    }
  }, [apiCall]);

  // Store logout function in ref
  logoutRef.current = logout;

  // Update user profile
  const updateProfile = useCallback(async (updateData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      if (response.success && response.user) {
        const updatedUser = { ...user, ...response.user };
        setUser(updatedUser);
        localStorage.setItem('authUser', JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      const errorMessage = error.message || 'Profile update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [user, apiCall]);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword })
      });

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (error) {
      const errorMessage = error.message || 'Password change failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Forgot password
  const forgotPassword = useCallback(async (email) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall('/auth/forgot-password', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ email })
      });

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Password reset failed');
      }
    } catch (error) {
      const errorMessage = error.message || 'Password reset failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Reset password
  const resetPassword = useCallback(async (token, newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall('/auth/reset-password', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ token, newPassword })
      });

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Password reset failed');
      }
    } catch (error) {
      const errorMessage = error.message || 'Password reset failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Context value
  const value = {
    // State
    user,
    loading,
    error,
    isAuthenticated,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    clearError,
    hasRole,
    
    // Utils
    apiCall
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;